import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Paper,
  Group,
  Text,
  Textarea,
  Button,
  Alert,
  Badge,
  Loader,
  Accordion,
  Code,
  useMantineColorScheme
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDeviceFloppy,
  IconInfoCircle,
  IconHistory,
  IconCheck,
  IconBulb,
  IconAlertTriangle
} from '@tabler/icons-react';
import { aiKnowledgeApi, AISystemPrompt } from '@/api/aiKnowledge.api';

const SystemPromptTab = () => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const [promptText, setPromptText] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState<AISystemPrompt | null>(null);
  const [history, setHistory] = useState<AISystemPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPromptData();
  }, []);

  const fetchPromptData = async () => {
    try {
      setLoading(true);
      const [promptRes, historyRes] = await Promise.all([
        aiKnowledgeApi.getSystemPrompt(),
        aiKnowledgeApi.getPromptHistory().catch(() => ({ data: { success: true, data: [] } }))
      ]);

      if (promptRes.data.success && promptRes.data.data) {
        setCurrentPrompt(promptRes.data.data);
        setPromptText(promptRes.data.data.prompt_text);
      }

      if (historyRes.data.success) {
        setHistory(historyRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch prompt data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!promptText.trim()) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.systemPrompt.emptyPrompt'),
        color: 'red'
      });
      return;
    }

    try {
      setSaving(true);
      const response = await aiKnowledgeApi.updateSystemPrompt({
        prompt_text: promptText.trim()
      });

      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: t('aiKnowledge.systemPrompt.saved'),
          color: 'green',
          icon: <IconCheck size={18} />
        });
        setCurrentPrompt(response.data.data);
        setHasChanges(false);
        fetchPromptData();
      }
    } catch (error) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.saveFailed'),
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTextChange = (value: string) => {
    setPromptText(value);
    setHasChanges(value !== (currentPrompt?.prompt_text || ''));
  };

  const handleRestoreVersion = (prompt: AISystemPrompt) => {
    setPromptText(prompt.prompt_text);
    setHasChanges(true);
  };

  if (loading) {
    return <Group justify="center" py="xl"><Loader /></Group>;
  }

  return (
    <Stack gap="md">
      {/* Info about system prompt */}
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        <Text size="sm">{t('aiKnowledge.systemPrompt.description')}</Text>
      </Alert>

      {/* Default prompt info */}
      <Paper p="md" radius="md" withBorder>
        <Group gap="xs" mb="xs">
          <IconBulb size={18} color="var(--mantine-color-yellow-6)" />
          <Text fw={600} size="sm">{t('aiKnowledge.systemPrompt.defaultInfo')}</Text>
        </Group>
        <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
          {t('aiKnowledge.systemPrompt.defaultInfoDesc')}
        </Text>
      </Paper>

      {/* Editor */}
      <Paper p="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={600}>{t('aiKnowledge.systemPrompt.editorTitle')}</Text>
            <Text size="xs" c="dimmed">
              {currentPrompt
                ? `${t('aiKnowledge.systemPrompt.version')} ${currentPrompt.version} · ${new Date(currentPrompt.updated_at).toLocaleString()}`
                : t('aiKnowledge.systemPrompt.noCustomPrompt')
              }
            </Text>
          </div>
          {hasChanges && (
            <Badge color="yellow" variant="light">
              {t('aiKnowledge.systemPrompt.unsavedChanges')}
            </Badge>
          )}
        </Group>

        <Textarea
          placeholder={t('aiKnowledge.systemPrompt.placeholder')}
          value={promptText}
          onChange={(e) => handleTextChange(e.target.value)}
          minRows={12}
          maxRows={30}
          autosize
          styles={{
            input: {
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: '1.6'
            }
          }}
        />

        <Group justify="space-between" mt="md">
          <Text size="xs" c="dimmed">
            {promptText.length} {t('aiKnowledge.systemPrompt.characters')}
          </Text>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges || !promptText.trim()}
            variant="gradient"
            gradient={{ from: 'blue', to: 'violet' }}
          >
            {t('aiKnowledge.systemPrompt.save')}
          </Button>
        </Group>
      </Paper>

      {/* Tips */}
      <Alert variant="light" color="teal" icon={<IconBulb size={18} />} title={t('aiKnowledge.systemPrompt.tipsTitle')}>
        <Stack gap="xs" mt="xs">
          <Text size="sm">• {t('aiKnowledge.systemPrompt.tip1')}</Text>
          <Text size="sm">• {t('aiKnowledge.systemPrompt.tip2')}</Text>
          <Text size="sm">• {t('aiKnowledge.systemPrompt.tip3')}</Text>
          <Text size="sm">• {t('aiKnowledge.systemPrompt.tip4')}</Text>
          <Text size="sm">• {t('aiKnowledge.systemPrompt.tip5')}</Text>
        </Stack>
      </Alert>

      {/* Example */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="xs">{t('aiKnowledge.systemPrompt.exampleTitle')}</Text>
        <Code
          block
          style={{
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            background: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)'
          }}
        >
          {t('aiKnowledge.systemPrompt.exampleText')}
        </Code>
      </Paper>

      {/* Version history */}
      {history.length > 0 && (
        <Paper p="lg" radius="md" withBorder>
          <Group gap="xs" mb="md">
            <IconHistory size={18} />
            <Text fw={600}>{t('aiKnowledge.systemPrompt.historyTitle')}</Text>
          </Group>

          <Accordion variant="separated">
            {history.slice(0, 10).map((prompt) => (
              <Accordion.Item key={prompt.id} value={`v${prompt.version}`}>
                <Accordion.Control>
                  <Group justify="space-between" mr="md">
                    <Group gap="xs">
                      <Badge
                        size="sm"
                        variant={prompt.is_active ? 'filled' : 'light'}
                        color={prompt.is_active ? 'green' : 'gray'}
                      >
                        v{prompt.version}
                      </Badge>
                      {prompt.is_active && (
                        <Badge size="xs" color="green" variant="dot">
                          {t('aiKnowledge.systemPrompt.active')}
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {new Date(prompt.created_at).toLocaleString()}
                    </Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Text
                      size="sm"
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                      }}
                    >
                      {prompt.prompt_text.substring(0, 2000)}
                      {prompt.prompt_text.length > 2000 && '...'}
                    </Text>
                    {!prompt.is_active && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => handleRestoreVersion(prompt)}
                      >
                        {t('aiKnowledge.systemPrompt.restore')}
                      </Button>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>
      )}

      {/* Delay notice */}
      <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={18} />}>
        <Text size="sm">{t('aiKnowledge.systemPrompt.delayNotice')}</Text>
      </Alert>
    </Stack>
  );
};

export default SystemPromptTab;
